import { useMemo } from 'react';

import { withLocaleString } from '@keaze/web/utils/withLocaleString';
import { TableData } from './startRegistration.types';
import { ListingDetails } from '@keaze/web/common/interfaces';

type UseStartRegistrationExpected = {
  price: number | null;
  deposit: number | null;
  rows: TableData[];
};

type UseStartRegistration = (
  listing: ListingDetails
) => UseStartRegistrationExpected;

type ConvertValueToPrice = (
  value: number | null,
  isPrice?: boolean
) => number | string | null;

export const convertValueToPrice: ConvertValueToPrice = (
  value,
  isPrice = true
) => {
  if (value !== null && isPrice) {
    return `£${withLocaleString(value)}`;
  }

  return value;
};

export const useStartRegistration: UseStartRegistration = (listing) => {
  const forSalePrice =
    listing.forSale && listing.isSharedOwnership
      ? listing.minShareValue
      : listing.fullMarketPrice;
  const monthlyRentValue = listing.forSale
    ? listing.subsidisedMonthlyRent
    : listing.monthlyRent;

  const price = listing.forSale ? forSalePrice : listing.weeklyRent;
  const deposit = listing.forSale ? listing.depositValue : listing.monthlyRent;

  const rows: TableData[] = useMemo(() => {
    const data = [
      {
        name: `${convertValueToPrice(
          listing.minSharePercentage,
          false
        )}% Share from`,
        value: convertValueToPrice(listing.minShareValue),
        hint:
          "The smallest portion of this property you can buy. You pay rent on the remaining portion. It's sometimes possible to buy a larger share than this, depending on your circumstances and the conditions on the property.",
      },
      {
        name: 'Full market price',
        value: convertValueToPrice(listing.fullMarketPrice),
        hint:
          "This is the total price of the property. When working out a share price, it'll be taken as a percentage of this value.",
      },
      {
        name: 'Minimum deposit',
        value: convertValueToPrice(listing.depositValue),
      },
      { name: 'Monthly rent', value: monthlyRentValue },
      {
        name: 'Monthly service charge',
        value: convertValueToPrice(listing.monthlyServiceCharge),
      },
      {
        name: 'Annual ground rent',
        value: convertValueToPrice(listing.annualGroundRent),
      },
      {
        name: 'Reservation free',
        value: convertValueToPrice(listing.reservationFee),
      },
      {
        name: 'Administration free',
        value: convertValueToPrice(listing.administrationFee),
      },
    ];
    const dataWithoutEmptyValue = data.filter((item) => item.value !== null);

    if (listing.singleProperty && listing.isSharedOwnership) {
      return dataWithoutEmptyValue;
    }

    if (listing.singleProperty && !listing.isSharedOwnership) {
      return dataWithoutEmptyValue.slice(1);
    }

    return [];
  }, [listing, monthlyRentValue]);

  return {
    price,
    deposit,
    rows,
  };
};
